// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import isEmpty from 'lodash/isEmpty';
import differenceBy from 'lodash/differenceBy';
import groupBy from 'lodash/groupBy';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import {VisibilityChangeEvent} from 'common/components/base/stackable';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {Permission} from 'common/report/constants';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {currentProjectStore} from 'common/report/stores/project-details';
import {userDetailsStore} from 'common/report/stores/user-details';
import {getProjectId} from 'common/report/util';
import {
  allowStaffOverride,
  isAllowedSuperadminOverride,
  isSharingEnabledForProject,
} from 'common/report/util/saved-entities';
import {emitInfraTrackEvent, getCustomElementParents, KEY_VALUES} from 'common/util/dom';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {trackHotjarEvent} from 'common/util/hotjar';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {PERFORMANCE_EVENT_PREFIX} from 'common/util/timing';
import {ProjectSelectedEvent} from 'common/widgets/target-project-selector-menu';
import {BoardMoveSetting} from 'common/report/queries/app-api/project-metadata-client';
import type {SelectDropdown} from 'common/components/select';
import type {IronContextRegistry} from 'common/report/context-registry';
import type {Dashboard} from 'common/report/dashboards/types';
import {DashboardType} from 'common/report/dashboards/types';
import type {Project} from 'common/report/queries/app-api/project-metadata-client';
import {isNestedDashboardsEnabled} from 'common/report/util/dashboards/utils';
import {Translatable} from 'common/i18n';

import {template} from './template';
import css from './index.inline.styl';

const YOUR_DASHBOARDS_SECTION = `Your boards`;
const SHARED_WITH_YOU_SECTION = `Shared with you`;
const ALL_DASHBOARDS_SECTION = `All boards`;

interface DashboardSelectorItem {
  dashboard: Dashboard;
  sublabelSearchTokens: string[];
  matchScore: number;
  titleSearchTokens: string[];
}

export enum DashboardDisabledStatus {
  EditAccessNeeded,
  EditorAccessNeeded,
  NestingLimitRestriction,
  CircularNestingRestriction,
}

interface DashboardSelectorState {
  activeItem: DashboardSelectorItem;
  busy: boolean;
  controller: DashboardSelectorController;
  creating: boolean;
  dashboards: Dashboard[];
  disableCreateDashboard: boolean;
  initialSortedDashboardIds: number[];
  loaded: boolean;
  moveToTargetProject: Project | null;
  isNestedBoardsEnabledForTargetProject: boolean;
  newTitle: string;
  open: boolean;
  scrollToId: number;
  searchFilter: string;
  sections: Array<{title: string; items: DashboardSelectorItem[]}>;
  excludedDashboards: number[];
  descendantDashboardIds: Set<number>;
  currentDashboardHierarchyTreeHeight: number;
}

interface DashboardSelectorProps {
  attrs?: {
    'accept-view-permissions'?: boolean;
    'create-dashboard-emits-event'?: boolean;
    'compact-mode'?: boolean;
    direction?: Directions;
    'disable-create-dashboard'?: boolean;
    'display-only-editable-dashboards'?: boolean;
    'hide-dashboard-list'?: boolean;
    'enable-management'?: boolean;
    'enable-nested-dashboards-restrictions'?: boolean;
    offset?: number;
    open: boolean;
    // Should only be used for storybook tests to make it easier to see
    'position-strategy'?: PositioningStrategy;
    'quick-create'?: boolean;
    'quick-create-save-dashboard-copy'?: string;
    'search-placeholder'?: string;
    'show-creator-sublabel'?: boolean;
    'show-move-out-button'?: boolean;
    'show-project-selector'?: boolean;
    'single-section'?: boolean;
    'user-id'?: number;
  };
  params?: {
    searchPlaceholder?: Translatable;
    quickCreateSaveDashboardCopy?: Translatable;
    detailedExplainCopy?: {
      title: Translatable;
      description: Translatable;
    };
  };
  props?: {
    controller?: DashboardSelectorController;
    initialSortedDashboardIds?: number[];
    excludedDashboards?: number[];
    currentDashboardHierarchyTreeHeight?: number;
    descendantDashboardIds?: Set<number>;
  };
  on?: {
    create?: (ev: CustomEvent) => void;
    dashboardadded?: (ev: CustomEvent<{id: number; userId: number}>) => void;
    dashboardchanged?: (ev: CustomEvent<{id: number}>) => void;
    manage?: () => void;
    menuchange?: (ev: VisibilityChangeEvent) => void;
    select?: (ev: CustomEvent<{id: number; userId: number}>) => void;
  };
}

export class DashboardSelector extends Component<
  DashboardSelectorState,
  DashboardSelectorProps[`attrs`],
  EmptyObj,
  EmptyObj,
  IronContextRegistry,
  DashboardSelectorProps[`params`]
> {
  public declare searchInputEl: HTMLInputElement;
  public declare createInputEl: HTMLInputElement;

  private declare _controllerListener: () => any;
  private declare _handleKeyDown: (ev: any) => void;
  private currentProjectStoreCallback = null;

  static get attrsSchema(): AttrsSchema<DashboardSelectorProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'accept-view-permissions': `boolean`,
      'compact-mode': `boolean`,
      'create-dashboard-emits-event': `boolean`,
      direction: {type: `string`, default: Directions.Right, enum: Object.values(Directions)},
      'disable-create-dashboard': `boolean`,
      'display-only-editable-dashboards': {
        type: `boolean`,
        description: `instead of showing non-editable items via tooltip, hide them all together`,
      },
      'enable-management': `boolean`,
      'enable-nested-dashboards-restrictions': {
        type: `boolean`,
        description: `show tooltip warning and disable selection of nested dashboards when restrictions would be violated`,
      },
      'hide-dashboard-list': `boolean`,
      offset: {type: `number`, default: 4},
      open: `boolean`,
      'position-strategy': `string`,
      'quick-create': `boolean`,
      'quick-create-save-dashboard-copy': `string`,
      'search-placeholder': {type: `string`, default: `Save to board...`},
      'show-creator-sublabel': {type: `boolean`, description: `show creator as sublabel instead of description`},
      'show-move-out-button': {type: `boolean`, description: `show the move out of all boards button`},
      'show-project-selector': {type: `boolean`, description: `show the project selector dropdown menu`},
      'single-section': {type: `boolean`, description: `show everything as all boards rather than sectioned out`},
      'user-id': {type: `number`, description: `User ID of the user viewing selector`},
    };
  }

  constructor() {
    super();
    this._controllerListener = this.updateFromController.bind(this);
  }

  get config() {
    return {
      css,
      template,
      contexts: [`currentProject` as const, `projects` as const, `urlManager` as const, `i18n` as const],
      useShadowDom: true,
      params: {
        searchPlaceholder: Function,
        quickCreateSaveDashboardCopy: Function,
        detailedExplainCopy: Object,
      },
      defaultState: {
        activeItem: null, // active means highlighted, triggered by mouseover or arrow keys
        busy: false,
        controller: null,
        creating: false,
        dashboards: [],
        disableCreateDashboard: false,
        initialSortedDashboardIds: [],
        loaded: false,
        moveToTargetProject: null,
        newTitle: ``,
        open: false,
        scrollToId: null,
        searchFilter: ``,
        sections: [],
        excludedDashboards: [],
        descendantDashboardIds: new Set<number>(),
        currentDashboardHierarchyTreeHeight: 1,
        isNestedBoardsEnabledForTargetProject: false,
      },
      helpers: {
        handleMenuChange: async (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));

          if (ev.detail.state !== `closed`) {
            // menu opening
            this.focusSearch();
            if (this.controller && !this.controller.getLoaded()) {
              await this.controller.loadDashboards();
            }
          } else {
            // menu closing
            this.reset();
          }
        },
        handleSearchInput: (ev) => {
          const sections = this.getSections(this.state.dashboards, ev.target.value);
          this.update({
            searchFilter: ev.target.value,
            sections,
          });
          this.activateItemIfNeeded(sections);
        },
        handlePerfPoint: (ev: CustomEvent<{name: string; openedAt: number}>) => {
          if (this.state.controller?.getLoaded() && !this.state.busy) {
            const parents = [...getCustomElementParents(this, 2), this.tagName.toLowerCase()];
            const now = Date.now();
            emitInfraTrackEvent(this, {
              eventName: ev.detail.name,
              eventPrefix: PERFORMANCE_EVENT_PREFIX,
              props: {elapsedMs: now - ev.detail.openedAt, now, parentPath: parents.join(`>`), parents},
            });
          }
        },
        handleSearchInputInsertHook: (vnode) => {
          this.searchInputEl = vnode.elm;
        },
        handleCreateInputInsertHook: (vnode) => {
          this.createInputEl = vnode.elm;
        },
        handleManageClick: () => this.dispatchEvent(new CustomEvent(`manage`)),
        handleQuickCreateClick: () => {
          this.update({creating: true, newTitle: this.t(`boards.header.title-placeholder`)});
          this.focusCreateField();
        },
        handleCreateClick: () => {
          if (this.attr(`create-dashboard-emits-event`)) {
            this.dispatchEvent(new CustomEvent(`create`));
          } else {
            this.update({creating: true, newTitle: this.state.searchFilter});
            this.focusCreateField();
          }
        },
        handleCreateInput: (ev) => {
          this.update({newTitle: ev.target.value});
        },
        handleCreateSubmit: async () => {
          const {newTitle} = this.state;
          if (!isEmpty(newTitle)) {
            const dashboard = await this.createDashboard(newTitle);
            if (this.attr(`quick-create`)) {
              this.update({creating: false});
              this.dispatchEvent(
                new CustomEvent(`select`, {
                  detail: this.getVisibilityTrackingParams(dashboard),
                  cancelable: true,
                }),
              );
            }
          }
        },
        handleItemRowInsertHook: (vnode) => {
          const itemRowEl = vnode.elm;
          if (!this.attr(`disable-create-dashboard`) && itemRowEl.offsetParent && vnode.key === this.state.scrollToId) {
            this.update({scrollToId: null});
            window.requestAnimationFrame(() => {
              // under test the item row sometimes no longer exists
              if (itemRowEl?.offsetParent) {
                itemRowEl.offsetParent.scrollTop =
                  itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
              }
            });
          }
        },
        getQuickCreateSaveText: () => {
          return this.print(this.params.quickCreateSaveDashboardCopy) || this.t(`reports.actions.saving.save-to-board`);
        },
        getSearchPlaceholder: () => {
          return (
            this.print(this.params.searchPlaceholder) || this.t(`reports.actions.saving.save-to-board-placeholder`)
          );
        },
        isSharingEnabled: () => {
          return isSharingEnabledForProject(currentProjectStore.value);
        },
        isDashboardDisabledDueToEditPermissions: (item) => {
          // TODO: junhouse implement break glass UX for sharing enabled projects
          /* eslint-disable camelcase */
          const canView = this.attr(`accept-view-permissions`) && (item.dashboard?.can_view || item.dashboard?.canView);
          const canEdit = item.dashboard?.can_update_basic || item.dashboard?.canUpdateBasic;
          /* eslint-enable camelcase */
          return !(canView || canEdit) && !isAllowedSuperadminOverride(item.dashboard);
        },
        isDashboardDisabledDueToTemplate: (item) => {
          return item.dashboard?.generation_type === DashboardType.StagedTemplate;
        },
        isCrossProjectEnabled: (): boolean => {
          const setting =
            this.getContext(`currentProject`).get()?.value?.crossProjectBoardMovingSetting ||
            BoardMoveSetting.NOT_ALLOWED;
          return setting !== BoardMoveSetting.NOT_ALLOWED && this.attr(`show-project-selector`);
        },
        isNestedBoardsEnabled: (): boolean => {
          return isNestedDashboardsEnabled(this.getContext(`currentProject`).get()?.value);
        },
        // Get the number of ancestors above dashboard in the dashboard hierarchy tree
        getDashboardAncestorsCount: (dashboard: Dashboard) => {
          if (!dashboard.parent_dashboard_id) {
            return 0;
          }

          const parentDashboard = this.state.dashboards.find(
            (searchedDashboard) => searchedDashboard.id === dashboard.parent_dashboard_id,
          );

          if (!parentDashboard || !parentDashboard.parent_dashboard_id) {
            return 1;
          }

          return 2;
        },
        getDashboardDisabledStatus: (item): DashboardDisabledStatus | null => {
          if (this.helpers.isDashboardDisabledDueToEditPermissions(item)) {
            if (this.helpers.isSharingEnabled()) {
              return DashboardDisabledStatus.EditorAccessNeeded;
            } else {
              return DashboardDisabledStatus.EditAccessNeeded;
            }
          }

          if (this.attr(`enable-nested-dashboards-restrictions`)) {
            if (this.state.descendantDashboardIds.has(item.dashboard.id)) {
              return DashboardDisabledStatus.CircularNestingRestriction;
            } else if (
              this.state.currentDashboardHierarchyTreeHeight + this.helpers.getDashboardAncestorsCount(item.dashboard) >
              2
            ) {
              return DashboardDisabledStatus.NestingLimitRestriction;
            }
          }

          return null;
        },
        getMessageForDisabledStatus: (status: DashboardDisabledStatus): string | null => {
          switch (status) {
            case DashboardDisabledStatus.EditorAccessNeeded:
              return this.t(`boards.selector.editor-access-needed`);
            case DashboardDisabledStatus.EditAccessNeeded:
              return this.t(`boards.selector.edit-access-needed`);
            case DashboardDisabledStatus.NestingLimitRestriction:
              return this.t(`boards.selector.nesting-limit-restriction`);
            case DashboardDisabledStatus.CircularNestingRestriction:
              return this.t(`boards.selector.circular-nesting-restriction`);
          }
        },
        // TODO opslead
        getItemActionHandler: (item) => {
          return async () => {
            const shouldChangeDashboard = this.dispatchEvent(
              new CustomEvent(`select`, {
                detail: this.getVisibilityTrackingParams(item.dashboard),
                cancelable: true,
              }),
            );
            if (shouldChangeDashboard) {
              await this.controller.changeActiveDashboard(item.dashboard.id);
              this.dispatchEvent(new CustomEvent(`dashboardchanged`, {detail: {id: item.dashboard.id}}));
            }
          };
        },
        getItemMouseOverHandler: (item) => {
          return () => this.update({activeItem: item});
        },
        getMoveToTargetProject: (): Project | null => {
          return this.state.moveToTargetProject;
        },
        moveBoard: (): void => {
          trackHotjarEvent(`Move to Your Boards clicked`);
          this.dispatchEvent(
            new CustomEvent(`select`, {
              detail: {
                targetProjectId: this.state.moveToTargetProject?.id,
              },
              cancelable: true,
            }),
          );
        },
        updateDashboardSelectorMenu: (ev: ProjectSelectedEvent): void => {
          const targetProjectId = ev.detail.projectId;
          const isNestedBoardsEnabled = ev.detail.isNestedDashboardsEnabled;
          this.update({
            moveToTargetProject: this.getContext(`projects`).getOne(targetProjectId).value,
            isNestedBoardsEnabledForTargetProject: isNestedBoardsEnabled,
          });
          if (this.state.isNestedBoardsEnabledForTargetProject) {
            this.controller = new DashboardSelectorController({
              projectId: targetProjectId,
              bookmarkID: null,
              dashboardsClient,
              i18n: this.getContext(`i18n`),
            });
          } else {
            this.controller = null;
          }

          if (this.controller && !this.controller.getLoaded()) {
            this.controller.loadDashboards();
          }
          this.el.querySelector<SelectDropdown>(`.mp-dashboard-selector-project-selection`)?.setDropMenuIsOpen(false);
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  getVisibilityTrackingParams(dashboard) {
    return {
      id: dashboard.id,
      targetProjectId: this.state.moveToTargetProject?.id || undefined,
      userId: dashboard.creator_id,
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `open`:
        this.update({[attr]: this.attr(attr)});
        break;
      case `disable-create-dashboard`:
        this.update({disableCreateDashboard: this.attr(attr)});
        break;
    }
  }

  async createDashboard(title: string) {
    this.update({busy: true});
    const dashboard = await this.controller.createDashboard({title});
    this.dispatchEvent(new CustomEvent(`create`, {detail: dashboard}));
    this.update({busy: false, creating: false});
    return dashboard;
  }

  scrollActiveItemIntoView() {
    const itemRowEl: HTMLElement = this.el.querySelector(`.mp-dashboard-selector-active`);
    const bottomScrollOffset = itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
    if (itemRowEl.offsetParent.scrollTop > itemRowEl.offsetTop) {
      itemRowEl.offsetParent.scrollTop = itemRowEl.offsetTop;
    } else if (itemRowEl.offsetParent.scrollTop < bottomScrollOffset) {
      itemRowEl.offsetParent.scrollTop = bottomScrollOffset;
    }
  }

  keyNavigateActiveItem(key) {
    const {activeItem, sections} = this.state;
    if (activeItem) {
      const sectionIdx = sections.findIndex((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
      const itemIdx = sections[sectionIdx].items.findIndex((it) => it.dashboard.id === activeItem.dashboard.id);

      let newActiveItem = null;
      switch (key) {
        case KEY_VALUES.ARROW_UP: {
          newActiveItem =
            sections[sectionIdx].items[itemIdx - 1] || // previous item in section
            (sectionIdx > 0 && [...sections[sectionIdx - 1].items].pop()) || // or last item in previous section
            null;
          break;
        }
        case KEY_VALUES.ARROW_DOWN: {
          newActiveItem =
            sections[sectionIdx].items[itemIdx + 1] || // next item in section
            (sectionIdx < sections.length - 1 && sections[sectionIdx + 1].items[0]) || // or first item in next section
            null;
          break;
        }
        default: {
          throw new Error(`Invalid navigation key`);
        }
      }
      if (newActiveItem) {
        this.update({activeItem: newActiveItem});
      }
    } else {
      this.update({activeItem: sections[0].items[0]});
    }
    window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
  }

  connectedCallback() {
    super.connectedCallback();
    this.currentProjectStoreCallback = () => this.update();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();

    this._handleKeyDown = (ev) => {
      switch (ev.key) {
        case KEY_VALUES.ARROW_DOWN:
        case KEY_VALUES.ARROW_UP: {
          ev.preventDefault();
          this.keyNavigateActiveItem(ev.key);
          window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
          break;
        }
        case KEY_VALUES.ENTER: {
          if (this.state.activeItem) {
            if (this.attr(`quick-create`) && this.state.creating) {
              this.helpers.handleCreateSubmit();
            } else {
              this.helpers.getItemActionHandler(this.state.activeItem)();
            }
          }
          break;
        }
      }
    };
    this.addEventListener(`keydown`, this._handleKeyDown);
    this.update({
      moveToTargetProject: this.getContext(`currentProject`).get().value || null,
      isNestedBoardsEnabledForTargetProject: this.helpers.isNestedBoardsEnabled(),
    });
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    this.removeEventListener(`keydown`, this._handleKeyDown);
    this.controller = null; // trigger unsubscribe in controller setter
    super.disconnectedCallback();
  }

  reset() {
    this.update(pick(this.config.defaultState, [`activeItem`, `creating`, `newTitle`, `searchFilter`]));
  }

  focusSearch() {
    window.requestAnimationFrame(() => this.searchInputEl?.focus());
  }

  focusCreateField() {
    window.requestAnimationFrame(() => {
      this.createInputEl.focus();
      this.createInputEl.select();
    });
  }

  getDashboardSection(dashboard) {
    // TODO: once sharing goes to GA removes the Feature Flag check
    if (this.isYourDashboard(dashboard)) {
      return YOUR_DASHBOARDS_SECTION;
    } else if (this.helpers.isSharingEnabled() && dashboard.can_view) {
      return SHARED_WITH_YOU_SECTION;
    } else {
      return ALL_DASHBOARDS_SECTION;
    }
  }

  getSectionIndex(section) {
    switch (section) {
      case YOUR_DASHBOARDS_SECTION:
        return 1;
      case SHARED_WITH_YOU_SECTION:
        return 2;
      case ALL_DASHBOARDS_SECTION:
        return 3;
      default:
        return Number.MAX_VALUE;
    }
  }

  getSections(dashboards, searchFilter) {
    // TODO: once sharing is enabled globally remove the feature flag check
    if (!this.helpers.isSharingEnabled()) {
      dashboards = dashboards.filter((dashboard) => dashboard.can_view);
    }

    if (this.initialSortedDashboardIds.length > 0 && dashboards.length > 0) {
      const initiallySortedDashboards = this.initialSortedDashboardIds
        .map((dashboardId) => {
          return dashboards.find((dashboard) => dashboard.id === dashboardId);
        })
        // remove any dashboards that couldn't be viewed or don't exist
        .filter((dashboard) => !!dashboard?.id);
      dashboards = uniqBy([...initiallySortedDashboards, ...dashboards], (d) => d.id);
    }

    if (this.attr(`display-only-editable-dashboards`)) {
      dashboards = dashboards.filter(
        (dashboard) =>
          !this.helpers.isDashboardDisabledDueToEditPermissions({dashboard}) &&
          !this.helpers.isDashboardDisabledDueToTemplate({dashboard}),
      );
    }

    dashboards = dashboards.filter((dashboard) => !this.state.excludedDashboards.includes(dashboard.id));

    const items: DashboardSelectorItem[] = dashboards
      .map((dashboard) => {
        const titleMatch = fuzzyMatch(dashboard.title, searchFilter) || {matchParts: null, score: 0};
        const sublabelMatch = fuzzyMatch(this.getDashboardSublabel(dashboard), searchFilter) || {
          matchParts: null,
          score: 0,
        };

        return {
          titleSearchTokens: titleMatch.matchParts,
          sublabelSearchTokens: sublabelMatch.matchParts,
          matchScore: Math.max(titleMatch.score, sublabelMatch.score),
          dashboard,
        };
      })
      .filter((item) => item.titleSearchTokens || item.sublabelSearchTokens)
      .sort((a, b) => b.matchScore - a.matchScore);

    if (this.attr(`single-section`)) {
      return [{items, title: ``}];
    }

    const sectionMap = groupBy(items, (item) => this.getDashboardSection(item.dashboard));
    const isSuperAdmin = userDetailsStore.value.projects[getProjectId()]?.permissions.includes(
      Permission.EditLockedEntity,
    );

    if (this.helpers.isSharingEnabled()) {
      if (!isSuperAdmin && !allowStaffOverride(getProjectId())) {
        delete sectionMap[ALL_DASHBOARDS_SECTION];
      }
    } else {
      delete sectionMap[SHARED_WITH_YOU_SECTION];
    }

    return sortBy(Object.entries(sectionMap), ([section]) => this.getSectionIndex(section))
      .map(([section, sectionItems]) => ({
        title: section,
        items: sectionItems,
      }))
      .filter((section) => section.items.length > 0);
  }

  getDashboardSublabel(dashboard) {
    return this.attr(`show-creator-sublabel`) ? dashboard.creator_name : dashboard.description;
  }

  isYourDashboard(dashboard) {
    return dashboard.creator_id === this.attr(`user-id`);
  }

  activateItemIfNeeded(sections) {
    const {activeItem} = this.state;
    const hasActiveItem =
      activeItem && sections.some((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
    if (!hasActiveItem) {
      this.update({activeItem: sections.length > 0 ? sections[0].items[0] : null});
    }
  }

  updateFromController() {
    const dashboards: Dashboard[] = this.controller?.getDashboards() || [];
    const added = differenceBy(dashboards, this.state.dashboards, `id`);
    if (this.state.dashboards && added.length > 0) {
      this.update({scrollToId: added[0].id});
    }

    const sections = this.getSections(dashboards || [], this.state.searchFilter);
    this.update({
      dashboards,
      sections,
    });
    this.activateItemIfNeeded(sections);
  }

  get controller() {
    return this.state.controller;
  }

  set controller(value) {
    if (this.state.controller) {
      this.unregisterController(this.state.controller);
    }
    this.update({controller: value});
    if (value) {
      this.registerController(value);
      this.updateFromController();
    }
  }

  get initialSortedDashboardIds() {
    return this.state.initialSortedDashboardIds;
  }

  set initialSortedDashboardIds(value) {
    const shouldUpdate = value !== this.state.initialSortedDashboardIds;
    this.update({initialSortedDashboardIds: value});
    // initialSortedDashboardIds was being set to empty for some reason, need to refresh getSections to include the proper
    // values thus update from controller here
    if (shouldUpdate) {
      this.updateFromController();
    }
  }

  set excludedDashboards(value) {
    this.update({excludedDashboards: value});
  }

  set descendantDashboardIds(value) {
    this.update({descendantDashboardIds: value});
  }

  set currentDashboardHierarchyTreeHeight(value) {
    this.update({currentDashboardHierarchyTreeHeight: value});
  }

  registerController(controller) {
    controller.subscribeUpdates(this._controllerListener);
  }

  unregisterController(controller) {
    controller.unsubscribeUpdates(this._controllerListener);
  }
}

export const DashboardSelectorEl = defineAndMakeJsxFactory<DashboardSelectorProps>(
  `mp-dashboard-selector`,
  DashboardSelector,
);
export const DashboardSelectorReact = panel2react<DashboardSelectorProps>(`mp-dashboard-selector`, DashboardSelector);
